// src/components/managed-sources/GoogleAnalyticsAuthStep.tsx

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBrandGoogle,
  IconCheck,
  IconExternalLink,
  IconPlugConnected,
  IconShieldCheck,
  IconX,
} from "@tabler/icons-react";

import {
  getGoogleAuthUrl,
  exchangeGoogleToken,
  listGAProperties,
  testGoogleConnection,
} from "@/api/managedSources";
import { listAuthorizations } from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import {
  authorizationRenderOption,
  authorizationSelectData,
  isUsableByMe,
} from "@/components/authorizations/authorizationOwnership";
import type { GoogleAnalyticsProperty } from "@/types/managedSources";

type Props = {
  authorizationId: string | null;
  propertyId: string;
  propertyName: string;
  onAuthorizationChange: (id: string | null) => void;
  onPropertyChange: (id: string, name: string) => void;
  /** The "Connection name" the customer typed here, lifted so the wizard can
   *  propose it as the source name instead of overwriting it with a composed
   *  default. Fires on every keystroke; stays empty when an existing
   *  authorization is reused. */
  onConnectionNameChange?: (name: string) => void;
  onValidated: (valid: boolean) => void;
};

export default function GoogleAnalyticsAuthStep({
  authorizationId,
  propertyId,
  propertyName,
  onAuthorizationChange,
  onPropertyChange,
  onConnectionNameChange,
  onValidated,
}: Props) {
  // Existing authorizations
  const [existingAuths, setExistingAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);

  // Mode
  const [mode, setMode] = useState<"existing" | "new">("existing");

  // New auth form
  const [authName, setAuthName] = useState("");
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState("");

  // Properties
  const [properties, setProperties] = useState<GoogleAnalyticsProperty[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  // Test state
  const [testResult, setTestResult] = useState<{
    success: boolean;
    timezone?: string;
    currency?: string;
    display_name?: string;
  } | null>(null);
  const [testError, setTestError] = useState("");
  const [testing, setTesting] = useState(false);

  // OAuth popup message listener ref
  const popupRef = useRef<Window | null>(null);
  const exchangedRef = useRef(false);

  // Load existing Google authorizations
  useEffect(() => {
    async function load() {
      setLoadingAuths(true);
      try {
        const auths = await listAuthorizations({ type: "GOOGLE" });
        setExistingAuths(auths);
        // Only rows I may use count (ADR-0329): a list of colleagues'
        // personal connections alone starts on "Create new connection".
        if (!auths.some(isUsableByMe)) {
          setMode("new");
        }
      } catch {
        // Non-fatal
      } finally {
        setLoadingAuths(false);
      }
    }
    load();
  }, []);

  // Validate when authorization + property are set
  useEffect(() => {
    onValidated(!!authorizationId && !!propertyId.trim());
  }, [authorizationId, propertyId, onValidated]);

  // Listen for OAuth popup messages
  const handleOAuthMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "google_oauth_callback") return;

      const { code, error } = event.data;
      popupRef.current = null;

      if (error) {
        setOauthError(`Google authorization failed: ${error}`);
        setOauthLoading(false);
        return;
      }

      if (!code) {
        setOauthError("No authorization code received from Google.");
        setOauthLoading(false);
        return;
      }

      // Prevent duplicate exchange calls (React Strict Mode / double events)
      if (exchangedRef.current) return;
      exchangedRef.current = true;

      // Exchange code for tokens
      try {
        const result = await exchangeGoogleToken(code, authName.trim());
        onAuthorizationChange(result.authorization_id);
        setProperties(result.properties);
        if (result.properties.length === 1) {
          const p = result.properties[0];
          onPropertyChange(p.property_id, p.display_name);
        }
      } catch (e: any) {
        setOauthError(e?.message || "Failed to exchange authorization code");
      } finally {
        setOauthLoading(false);
      }
    },
    [authName, onAuthorizationChange, onPropertyChange]
  );

  useEffect(() => {
    window.addEventListener("message", handleOAuthMessage);
    return () => window.removeEventListener("message", handleOAuthMessage);
  }, [handleOAuthMessage]);

  async function handleConnectGoogle() {
    if (!authName.trim()) return;
    exchangedRef.current = false;
    setOauthLoading(true);
    setOauthError("");

    try {
      const { url } = await getGoogleAuthUrl();
      // Open popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        url,
        "google_oauth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      // Poll for popup close (user may close without completing)
      const checkInterval = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkInterval);
          if (oauthLoading) {
            setOauthLoading(false);
          }
        }
      }, 1000);
    } catch (e: any) {
      setOauthError(e?.message || "Failed to get authorization URL");
      setOauthLoading(false);
    }
  }

  async function handleSelectExistingAuth(authId: string | null) {
    onAuthorizationChange(authId);
    onPropertyChange("", "");
    setProperties([]);
    setTestResult(null);
    setTestError("");

    if (!authId) return;

    // Load properties for this authorization
    setPropertiesLoading(true);
    try {
      const result = await listGAProperties(authId);
      setProperties(result.properties);
      if (result.properties.length === 1) {
        const p = result.properties[0];
        onPropertyChange(p.property_id, p.display_name);
      }
    } catch (e: any) {
      setOauthError(e?.message || "Failed to load properties");
    } finally {
      setPropertiesLoading(false);
    }
  }

  async function handleTest() {
    if (!authorizationId || !propertyId) return;
    setTesting(true);
    setTestResult(null);
    setTestError("");
    try {
      const result = await testGoogleConnection(authorizationId, propertyId);
      setTestResult(result);
    } catch (e: any) {
      setTestError(e?.message || "Connection test failed");
    } finally {
      setTesting(false);
    }
  }

  const propertySelectData = properties.map((p) => ({
    value: p.property_id,
    label: p.account_name
      ? `${p.display_name} (${p.account_name})`
      : p.display_name,
  }));

  const existingAuthOptions = authorizationSelectData(existingAuths, {
    label: (a) =>
      `${a.name}${a.config?.property_count ? ` (${a.config.property_count} properties)` : ""}`,
  });

  if (loadingAuths) {
    return (
      <Stack align="center" justify="center" py={60}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading authorizations...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Hero section */}
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="blue">
          <IconBrandGoogle size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect Google Analytics
          </Text>
          <Text size="sm" c="dimmed">
            Link your GA4 property to start syncing analytics data
            automatically.
          </Text>
        </Stack>
      </Group>

      {/* Mode selector */}
      {existingAuths.length > 0 && (
        <SegmentedControl
          value={mode}
          onChange={(v) => {
            setMode(v as "existing" | "new");
            if (v === "new") {
              onAuthorizationChange(null);
              onPropertyChange("", "");
              setProperties([]);
              setTestResult(null);
            }
          }}
          data={[
            { label: "Use existing connection", value: "existing" },
            { label: "Create new connection", value: "new" },
          ]}
          fullWidth
        />
      )}

      {/* Existing authorization */}
      {mode === "existing" && existingAuths.length > 0 && (
        <Stack gap="md">
          <Select
            label="Google authorization"
            placeholder="Select a saved connection"
            data={existingAuthOptions}
            renderOption={authorizationRenderOption(existingAuths)}
            value={authorizationId}
            onChange={handleSelectExistingAuth}
            size="md"
          />

          {propertiesLoading && (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">Loading properties...</Text>
            </Group>
          )}

          {authorizationId && properties.length > 0 && (
            <Select
              label="GA4 Property"
              placeholder="Select a property"
              data={propertySelectData}
              value={propertyId || null}
              onChange={(val) => {
                const prop = properties.find((p) => p.property_id === val);
                onPropertyChange(val || "", prop?.display_name || "");
                setTestResult(null);
              }}
              searchable
              size="md"
            />
          )}

          {authorizationId && propertyId && (
            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                borderColor: "var(--mantine-color-green-4)",
                backgroundColor: "var(--mantine-color-green-0)",
              }}
            >
              <Group gap="sm">
                <ThemeIcon
                  size={32}
                  color="green"
                  variant="light"
                  radius="xl"
                >
                  <IconPlugConnected size={16} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text size="sm" fw={600}>
                    Connected
                  </Text>
                  <Text size="xs" c="dimmed">
                    Property: {propertyName || propertyId}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          )}
        </Stack>
      )}

      {/* New authorization */}
      {mode === "new" && (
        <Stack gap="md">
          {/* Connection name */}
          <TextInput
            label="Connection name"
            placeholder="e.g. My Google Analytics"
            value={authName}
            onChange={(e) => {
              setAuthName(e.currentTarget.value);
              onConnectionNameChange?.(e.currentTarget.value);
            }}
            required
            size="md"
            disabled={!!authorizationId}
          />

          {/* OAuth button */}
          {!authorizationId && (
            <>
              <Button
                variant="outline"
                leftSection={<IconBrandGoogle size={18} />}
                rightSection={<IconExternalLink size={14} />}
                onClick={handleConnectGoogle}
                loading={oauthLoading}
                disabled={!authName.trim()}
                size="md"
                fullWidth
              >
                Sign in with Google
              </Button>
            </>
          )}

          {oauthError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {oauthError}
            </Alert>
          )}

          {/* Property selection (after successful auth) */}
          {authorizationId && properties.length > 0 && (
            <Select
              label="GA4 Property"
              placeholder="Search or select a property"
              data={propertySelectData}
              value={propertyId || null}
              onChange={(val) => {
                const prop = properties.find((p) => p.property_id === val);
                onPropertyChange(val || "", prop?.display_name || "");
                setTestResult(null);
              }}
              searchable
              size="md"
            />
          )}

          {authorizationId && properties.length === 0 && !propertiesLoading && (
            <Alert color="orange">
              No GA4 properties found for this Google account. Make sure the
              account has access to at least one GA4 property.
            </Alert>
          )}

          {/* Test connection */}
          {authorizationId && propertyId && (
            <>
              <Divider />
              <Group gap="sm">
                <Button
                  variant="outline"
                  onClick={handleTest}
                  loading={testing}
                  leftSection={<IconShieldCheck size={16} />}
                  size="md"
                >
                  Test connection
                </Button>
              </Group>
            </>
          )}

          {testError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {testError}
            </Alert>
          )}

          {testResult && (
            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                borderColor: testResult.success
                  ? "var(--mantine-color-green-4)"
                  : "var(--mantine-color-red-4)",
                backgroundColor: testResult.success
                  ? "var(--mantine-color-green-0)"
                  : "var(--mantine-color-red-0)",
              }}
            >
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon
                    size={20}
                    color={testResult.success ? "green" : "red"}
                    variant="filled"
                    radius="xl"
                  >
                    {testResult.success ? (
                      <IconCheck size={12} />
                    ) : (
                      <IconX size={12} />
                    )}
                  </ThemeIcon>
                  <Text size="sm" fw={500}>
                    {testResult.success
                      ? "Connection successful"
                      : "Connection failed"}
                  </Text>
                </Group>
                {testResult.success && (testResult.timezone || testResult.currency) && (
                  <Group gap="xs" pl={28}>
                    <Text size="xs" c="dimmed">
                      {[
                        testResult.display_name,
                        testResult.timezone,
                        testResult.currency,
                      ]
                        .filter(Boolean)
                        .join(" / ")}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Paper>
          )}

          {/* Saved confirmation */}
          {authorizationId && propertyId && (
            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                borderColor: "var(--mantine-color-green-4)",
                backgroundColor: "var(--mantine-color-green-0)",
              }}
            >
              <Group gap="sm">
                <ThemeIcon
                  size={32}
                  color="green"
                  variant="light"
                  radius="xl"
                >
                  <IconPlugConnected size={16} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text size="sm" fw={600}>
                    Connection saved
                  </Text>
                  <Text size="xs" c="dimmed">
                    Property: {propertyName || propertyId}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          )}
        </Stack>
      )}
    </Stack>
  );
}
